import clsx from "clsx";
import {
  forwardRef,
  useId,
  useReducer,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import Icon, { type IconName } from "../components/Icon";

/* ==========================================================================
   Обёртка поля: подпись, подсказка, ошибка
   ========================================================================== */

export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
  htmlFor,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
  htmlFor?: string;
}) {
  // Без htmlFor подпись оборачивает контрол: связь неявная, но настоящая —
  // иначе у поля не будет доступного имени.
  //
  // Подсказка и ошибка — ВНЕ подписи. Раньше обёртка захватывала и их, и
  // имя поля становилось «Роль Оператор подразделения: инициатор заявок…»
  // — экранный чтец зачитывал всю подсказку вместо слова «Роль».
  // Звёздочка обязательности скрыта от чтеца: он слышит «обязательное»
  // по атрибуту required, а «Email звёздочка» — шум.
  const Wrapper = label && !htmlFor ? "label" : "div";
  const Caption = htmlFor ? "label" : "span";

  return (
    <div className={clsx("block min-w-0", className)}>
      <Wrapper className="block">
        {label && (
          <Caption htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-muted">
            {label}
            {required && (
              <span className="ml-0.5 text-danger" aria-hidden="true">
                *
              </span>
            )}
          </Caption>
        )}
        {children}
      </Wrapper>
      {error ? (
        <p className="mt-1 flex items-center gap-1 text-xs text-danger">
          <Icon name="alert" size={12} />
          {error}
        </p>
      ) : (
        hint && <p className="mt-1 text-xs text-faint">{hint}</p>
      )}
    </div>
  );
}

/* ==========================================================================
   Контролы
   ========================================================================== */

const control =
  "block w-full rounded-md border bg-surface text-sm text-ink shadow-xs transition-colors " +
  "placeholder:text-faint hover:border-line-strong " +
  "focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25 " +
  "disabled:bg-sunken disabled:text-faint disabled:cursor-not-allowed";

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean; icon?: IconName }
>(function Input({ className, invalid, icon, ...rest }, ref) {
  const field = (
    <input
      ref={ref}
      {...rest}
      className={clsx(
        control,
        "h-8 px-2.5",
        icon && "pl-8",
        invalid ? "border-danger" : "border-line",
        className,
      )}
    />
  );
  if (!icon) return field;
  return (
    <div className="relative">
      <Icon
        name={icon}
        size={15}
        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-faint"
      />
      {field}
    </div>
  );
});

/** Разрешённое содержимое десятичного поля на любом шаге набора. */
const DECIMAL = /^-?\d*(?:\.\d*)?$/;

/** Приводит набранное к каноничному виду или отвергает. `null` — не принимать.
 *
 *  Пустая строка, одинокий минус и «0.» проходят: это промежуточные состояния
 *  набора. Их отбраковывает валидация формы, а не поле ввода. */
function normalizeDecimal(text: string): string | null {
  // `\s` в JS уже включает неразрывный и узкий пробелы — разделители разрядов.
  const canonical = text.replace(/\s/g, "").replace(",", ".");
  return DECIMAL.test(canonical) ? canonical : null;
}

/** Поле для суммы, цены и количества.
 *
 * `type="number"` для этого не годится: в русской локали Chrome молча теряет
 * запятую — набранное «1234,56» превращается в «123456». Поле при этом
 * выглядит корректно заполненным, а сумма вырастает в сто раз. Недонабранное
 * значение («-», «1e») браузер отдаёт как пустую строку, из-за чего
 * контролируемое поле теряет введённое.
 *
 * Здесь `type="text"` с `inputMode="decimal"`: цифровая клавиатура на телефоне
 * остаётся, а разбор берём на себя. Запятая становится точкой, пробелы-
 * разделители разрядов отбрасываются, посторонний символ не проходит вовсе.
 *
 * Значение наружу всегда каноничное — с точкой. Поэтому вызывающий код как
 * работал со строкой и `Number(...)`, так и работает.
 */
export const DecimalInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "onChange" | "step" | "min" | "max"> & {
    value: string;
    onChange: (value: string) => void;
    invalid?: boolean;
  }
>(function DecimalInput({ className, invalid, value, onChange, ...rest }, ref) {
  const [, redraw] = useReducer((n: number) => n + 1, 0);
  return (
    <input
      ref={ref}
      {...rest}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={value}
      onChange={(e) => {
        const next = normalizeDecimal(e.target.value);
        if (next !== null && next !== value) {
          onChange(next);
          return;
        }
        // Значение не изменилось: либо символ отвергнут, либо он ничего не
        // поменял. React в этом случае не перерисует поле, и лишний символ
        // остался бы на экране. Писать в el.value нельзя — это сбивает
        // внутренний учёт значения у React, и следующая правка может
        // потеряться. Поэтому просто просим перерисовку: React сам вернёт
        // в поле то, что считает истиной.
        redraw();
      }}
      className={clsx(
        control,
        "h-8 px-2.5",
        invalid ? "border-danger" : "border-line",
        className,
      )}
    />
  );
});

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function Textarea({ className, invalid, rows = 3, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      {...rest}
      className={clsx(
        control,
        "px-2.5 py-1.5 leading-relaxed resize-y",
        invalid ? "border-danger" : "border-line",
        className,
      )}
    />
  );
});

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }
>(function Select({ className, invalid, children, ...rest }, ref) {
  return (
    <div className="relative">
      <select
        ref={ref}
        {...rest}
        className={clsx(
          control,
          "h-8 cursor-pointer appearance-none pl-2.5 pr-7",
          invalid ? "border-danger" : "border-line",
          className,
        )}
      >
        {children}
      </select>
      <Icon
        name="chevronDown"
        size={14}
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-faint"
      />
    </div>
  );
});

/* ==========================================================================
   Флажок и переключатель
   ========================================================================== */

export function Checkbox({
  label,
  description,
  className,
  ...rest
}: { label?: ReactNode; description?: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  const inputId = rest.id ?? id;
  return (
    <label
      htmlFor={inputId}
      className={clsx(
        "inline-flex items-start gap-2 text-sm text-ink select-none",
        // Без подписи в клик попадает только сам квадрат 16×16 — мало
        // и мышью, и тем более пальцем. Отступ расширяет зону нажатия
        // до 24×24, отрицательное поле не даёт ему сдвинуть вёрстку.
        !label && !description && "p-1 -m-1",
        rest.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        className,
      )}
    >
      {/* Нативный контрол, а не скрытый input: он остаётся доступным для
          клавиатуры, скринридера и автотестов, а вид задаём сами. */}
      <input
        type="checkbox"
        id={inputId}
        {...rest}
        className={clsx(
          "mt-px h-4 w-4 shrink-0 cursor-pointer appearance-none rounded border",
          "border-line-strong bg-surface transition-colors",
          "hover:border-accent",
          "checked:border-accent checked:bg-accent",
          "focus-visible:ring-2 focus-visible:ring-accent/40",
          "disabled:cursor-not-allowed",
          "bg-[length:0.875rem] bg-center bg-no-repeat",
          "checked:bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22none%22%20stroke%3D%22white%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m3.5%208.5%203%203%206-7%22%2F%3E%3C%2Fsvg%3E')]",
        )}
      />
      {(label || description) && (
        <span className="min-w-0">
          {label && <span className="block leading-4">{label}</span>}
          {description && <span className="block text-xs text-muted">{description}</span>}
        </span>
      )}
    </label>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
  className,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  // Кнопка ссылается на подпись явно: имя по обёртке <label> вычисляет
  // браузер, но не каждый инструмент — проверка доступных имён его не видела.
  const labelId = useId();
  return (
    <label
      className={clsx(
        "inline-flex cursor-pointer items-center gap-2 text-sm text-ink select-none",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={label ? labelId : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={clsx(
          "relative h-5 w-9 shrink-0 rounded-full border transition-colors",
          checked ? "border-accent bg-accent" : "border-line-strong bg-sunken",
        )}
      >
        <span
          className={clsx(
            "absolute left-0 top-0.5 h-3.5 w-3.5 rounded-full bg-surface shadow-sm transition-transform",
            checked ? "translate-x-[1.125rem]" : "translate-x-0.5",
          )}
        />
      </button>
      {label && <span id={labelId}>{label}</span>}
    </label>
  );
}

/* ==========================================================================
   Сегментированный переключатель — вместо радиокнопок для 2–4 вариантов
   ========================================================================== */

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  size = "md",
  className,
}: {
  value: T;
  onChange: (next: T) => void;
  options: { value: T; label: ReactNode; icon?: IconName; title?: string }[];
  size?: "sm" | "md";
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={clsx(
        "inline-flex items-center gap-0.5 rounded-md border border-line bg-sunken p-0.5",
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            role="tab"
            type="button"
            aria-selected={active}
            title={option.title}
            onClick={() => onChange(option.value)}
            className={clsx(
              "inline-flex items-center gap-1.5 rounded font-medium transition-colors",
              size === "sm" ? "h-6 px-2 text-xs" : "h-7 px-2.5 text-sm",
              active
                ? "bg-surface text-ink shadow-xs"
                : "text-muted hover:text-ink",
            )}
          >
            {option.icon && <Icon name={option.icon} size={14} />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
